import type { AccountInfo, PopupRequest, SilentRequest } from "@azure/msal-browser";
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  NavigationClient,
  PublicClientApplication,
} from "@azure/msal-browser";
import { config, scopes } from "@/core/config/msal-auth";

console.log("[MSAL] module loaded", import.meta.env.MODE, window.location.origin);

export type MaybeAccount = AccountInfo | null;

export const msal = new PublicClientApplication(config);

const initializePromise = (async () => {
  await msal.initialize();

  // Handle redirect response *once* on app start
  const redirectResult = await msal.handleRedirectPromise();
  if (redirectResult?.account) {
    msal.setActiveAccount(redirectResult.account);
    // optional: remember the account chosen
    sessionStorage.setItem("msal.activeHomeAccountId", redirectResult.account.homeAccountId);
  }
})();

function pickAccount(accounts: AccountInfo[]): AccountInfo | null {
  if (!accounts.length) return null;

  const active = msal.getActiveAccount();
  if (active) return active;

  if (accounts.length === 1) return accounts[0];

  const remembered = sessionStorage.getItem("msal.activeHomeAccountId");
  if (remembered) {
    const match = accounts.find((a) => a.homeAccountId === remembered);
    if (match) return match;
  }

  // fallback: choose first deterministically
  return accounts[0];
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  return new Error(typeof err === "string" ? err : "Unknown authentication error");
}

function resetMsalOnlySessionStorage() {
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (!k) continue;
    // conservative: remove MSAL-ish keys + your own msal state keys
    if (
      k.startsWith("msal.") ||
      k.startsWith("msal") ||
      k.startsWith("Microsoft.Authentication") ||
      k === "msal.activeHomeAccountId"
    ) {
      keysToRemove.push(k);
    }
  }
  keysToRemove.forEach((k) => sessionStorage.removeItem(k));
}

export const Auth = {
  async initialize(client?: NavigationClient): Promise<MaybeAccount> {
    await initializePromise;

    if (client) msal.setNavigationClient(client);

    const accounts = msal.getAllAccounts();
    const chosen = pickAccount(accounts);
    if (chosen) {
      this.setAccount(chosen);
      sessionStorage.setItem("msal.activeHomeAccountId", chosen.homeAccountId);
    }

    return msal.getActiveAccount();
  },

  // Redirect login does not "return an account" immediately; it leaves the page.
  async login(): Promise<MaybeAccount> {
    const request: PopupRequest = { scopes };

    try {
      await msal.loginRedirect(request);
      return null; // unreachable in normal redirect flow
    } catch (err: unknown) {
      // If MSAL gets wedged in interaction state, clear MSAL keys and try again once
      if (err instanceof BrowserAuthError && err.errorCode === "interaction_in_progress") {
        resetMsalOnlySessionStorage();
        await msal.loginRedirect(request);
        return null;
      }
      throw toError(err);
    }
  },

  async logout(): Promise<void> {
    const activeAccount = msal.getActiveAccount();
    const logoutHint = activeAccount?.idTokenClaims?.login_hint as string | undefined;

    try {
      await msal.logoutRedirect({ logoutHint });
    } catch {
      resetMsalOnlySessionStorage();
    }
  },

  async getToken(): Promise<string> {
    const account = msal.getActiveAccount() ?? pickAccount(msal.getAllAccounts()) ?? undefined;

    const request: SilentRequest = {
      scopes,
      account,
    };

    try {
      const result = await msal.acquireTokenSilent(request);
      return result.accessToken;
    } catch (err: unknown) {
      if (err instanceof InteractionRequiredAuthError) {
        const result = await msal.acquireTokenPopup({ scopes, account });
        return result.accessToken;
      }
      throw toError(err);
    }
  },

  setAccount(account: MaybeAccount): MaybeAccount {
    msal.setActiveAccount(account);
    if (account) sessionStorage.setItem("msal.activeHomeAccountId", account.homeAccountId);
    return account;
  },

  reset() {
    resetMsalOnlySessionStorage();
  },
};
